import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { ProductListingCardLogo } from '@deltek/specification-client/core/Widgets/ProductListing/Card/ProductListingCardLogo'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    darken,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    makeStyles,
    Theme,
    Typography,
} from '@material-ui/core'
import { InfoOutlined } from '@material-ui/icons'
import Close from '@material-ui/icons/Close'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch'
import React, { useCallback, useRef } from 'react'
import { CANCEL_TEXT } from '../constants'
import { AddProductToggleStates } from './Card/ProductListingCardPopperContainer'
import MoreInfoDialogModal from './MoreInfoDialogModal'
import { usePSEInfoDialogStore } from './store'

interface AddProductSelectionDialogEnhancedProps {
    open: boolean
    listingType: ListingLevel
    hasManufacturerOptions: boolean | undefined
    onMfrAndProductClick: (toggleStates: AddProductToggleStates) => void
    onMfrOnlyClick: (toggleStates: AddProductToggleStates) => void
    onPropOnlyClick: (toggleStates: AddProductToggleStates) => void
    onCancel: () => void
}

const sliderColor = '#1899D4'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            width: pxToRem(555),
            minWidth: pxToRem(550),
            color: theme.palette.common.black,
        },
    },
    title: {
        paddingBottom: theme.spacing(0.5),
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
    },
    icon: {
        display: 'inline',
        marginLeft: pxToRem(10),
        marginRight: 'auto',
        marginBottom: theme.spacing(1),
        width: pxToRem(20),
        height: pxToRem(20),
    },
    infoIcon: {
        display: 'inline',
        marginLeft: pxToRem(10),
        marginRight: pxToRem(10),
        marginBottom: pxToRem(5),
        marginTop: pxToRem(5),
        width: pxToRem(24),
        height: pxToRem(24),
        color: '#FF6C6C',
    },
    contentContainer: {
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 10,
        backgroundColor: '#F2F2F2',
        width: pxToRem(500),
        height: pxToRem(126),
        padding: theme.spacing(2),
    },
    infoContainerExpanded: {
        borderRadius: 10,
        border: '1px solid #FFBFBF',
        backgroundColor: '#FFFFFF2',
    },
    infoContainerCollapsed: {
        borderRadius: 10,
        border: '1px solid #FFBFBF',
        backgroundColor: '#FFBFBF26',
    },
    infoContainerTitle: {
        '& > .MuiAccordionSummary-content': {
            cursor: 'default',
        },
        '&.MuiAccordionSummary-root': {
            height: pxToRem(37),
            maxHeight: pxToRem(37),
            minHeight: pxToRem(37),
            paddingTop: theme.spacing(0),
            paddingBottom: theme.spacing(0),
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(0),
            margin: theme.spacing(0),
        },
    },
    infoContainerBody: {
        '&.MuiAccordionDetails-root': {
            paddingTop: theme.spacing(0),
            paddingBottom: theme.spacing(1),
            paddingRight: pxToRem(15),
            paddingLeft: pxToRem(15),
        },
    },
    queryContainer: {
        width: pxToRem(500),
        marginBottom: theme.spacing(1),
    },
    noteContainer: {
        height: pxToRem(150),
        marginBottom: theme.spacing(1),
    },
    mfrAndProdContainer: {
        height: pxToRem(196),
        marginBottom: theme.spacing(1),
    },
    mfrOnlyContainer: {
        height: pxToRem(196),
        marginBottom: theme.spacing(1),
    },
    propOnlyContainer: {
        height: pxToRem(176),
        marginBottom: theme.spacing(1),
    },
    mfrAndProdContainerNoMO: {
        height: pxToRem(171),
        marginBottom: theme.spacing(1),
    },
    mfrOnlyContainerNoMO: {
        height: pxToRem(171),
        marginBottom: theme.spacing(1),
    },
    propOnlyContainerNoMO: {
        height: pxToRem(131),
        marginBottom: theme.spacing(1),
    },
    rightContainer: {
        display: 'flex',
        padding: theme.spacing(0, 2),
    },
    contentSecondaryTitle: {
        color: '#304959',
        fontSize: pxToRem(18),
        fontWeight: 500,
        marginBottom: theme.spacing(0.5),
    },
    instructionText: {
        color: 'rgba(0, 0, 0, 0.54)',
        fontSize: pxToRem(14),
        fontWeight: 400,
        lineHeight: pxToRem(18),
    },
    button: {
        borderRadius: pxToRem(100),
        fontSize: pxToRem(14),
        width: pxToRem(130),
        height: pxToRem(32),
    },
    addToProjectButton: {
        backgroundColor: '#324958',
        color: theme.palette.common.white,
        display: 'flex',
        alignSelf: 'flex-end',
        '&:hover': {
            backgroundColor: darken('#F78909', 0.05),
        },
    },
    cancelButton: {
        backgroundColor: theme.palette.common.white,
        border: '1px solid #C9C9C9',
    },
    buttonContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        flexGrow: 1,
        alignItems: 'flex-end',
    },
    actionContainer: {
        paddingRight: theme.spacing(3),
        paddingBottom: theme.spacing(2.5),
    },
    sliderGridContainer: {
        paddingTop: theme.spacing(1.25),
        paddingBottom: theme.spacing(1.25),
        alignItems: 'center',
    },
    linkButton: {
        color: '#0098D6',
        fontWeight: 'normal',
        padding: theme.spacing(0, 0),
        '&:hover': {
            cursor: 'pointer',
            backgroundColor: 'transparent',
        },
        '&>.MuiButton-label': {
            display: 'unset',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
    },
    switchLabel: {
        alignContent: 'center',
        display: 'flex',
        paddingRight: '50%',
        flexWrap: 'wrap',
        height: '2.5rem',
        '&.MuiFormControlLabel-labelPlacementStart': {
            marginLeft: 0,
        },
        '& > .MuiFormControlLabel-label': {
            minWidth: pxToRem(200),
        },
    },
    switchContainer: {
        marginLeft: theme.spacing(-1),
        marginTop: theme.spacing(2),
    },
    dialogContent: {
        overflowX: 'hidden',
    },
    accordionWrapper: {
        padding: theme.spacing(1, 3),
    },
    expandIconWrapper: {
        padding: theme.spacing(0, 2),
    },
    dialogContentFontStyle: {
        fontSize: pxToRem(14),
        color: '#424242',
    },
}))

export const AddProductSelectionDialogEnhanced = ({
    open,
    listingType,
    hasManufacturerOptions,
    onMfrAndProductClick,
    onMfrOnlyClick,
    onPropOnlyClick,
    onCancel,
}: AddProductSelectionDialogEnhancedProps) => {
    const classes = useStyles()

    const isGeneralDistribution =
        listingType === ListingLevel.Premium ||
        listingType === ListingLevel.PremiumProprietary

    const [toggleStates, setToggleStates] = React.useState<
        AddProductToggleStates
    >({
        isMfrProdPropsSelected: true,
        isMfrProdOptionsSelected: false,
        isMfrOnlyPropsSelected: true,
        isMfrOnlyOptionsSelected: false,
        isPropOnlyOptionsSelected: false,
    })

    const updateToggleStates = (
        propertyName: string,
        updatedValue: boolean
    ) => {
        //Basic logic here is that the for the 'options' toggle to be true, the 'properties' toggle has to be true
        switch (propertyName) {
            case 'mfrAndProdProperties': {
                setToggleStates((past) => ({
                    ...past,
                    isMfrProdPropsSelected: updatedValue,
                    isMfrProdOptionsSelected: updatedValue
                        ? past.isMfrProdOptionsSelected
                        : false,
                }))
                break
            }
            case 'mfrAndProdOptions': {
                setToggleStates((past) => ({
                    ...past,
                    isMfrProdOptionsSelected: past.isMfrProdPropsSelected
                        ? updatedValue
                        : false,
                }))
                break
            }
            case 'mfrOnlyProperties': {
                setToggleStates((past) => ({
                    ...past,
                    isMfrOnlyPropsSelected: updatedValue,
                    isMfrOnlyOptionsSelected: updatedValue
                        ? past.isMfrProdOptionsSelected
                        : false,
                }))
                break
            }
            case 'mfrOnlyOptions': {
                setToggleStates((past) => ({
                    ...past,
                    isMfrOnlyOptionsSelected: past.isMfrOnlyPropsSelected
                        ? updatedValue
                        : false,
                }))
                break
            }
            case 'propOnlyOptions': {
                setToggleStates((past) => ({
                    ...past,
                    isPropOnlyOptionsSelected: updatedValue,
                }))
                break
            }
        }
    }

    const [
        isInfoExpanded,
        setIsInfoExpanded,
    ] = usePSEInfoDialogStore((state) => [
        !!state.isExpanded,
        state.setIsExpanded,
    ])

    const defaultExpanded = useRef(false)

    const [isOpenMoreInfoDialog, setIsOpenMoreInfoDialog] = React.useState(
        false
    )

    const handleMoreInfo = () => {
        setIsOpenMoreInfoDialog(true)
    }

    const toggleOne = (
        event: React.ChangeEvent<HTMLInputElement>,
        state: boolean
    ) => {
        updateToggleStates(event.target.name, state)
    }

    const handleClose = useCallback(
        (_, reason) => {
            // set this so the modal closes when escape is clicked but not when clicked on the backdrop
            if (reason === 'escapeKeyDown') {
                onCancel()
            }
        },
        [onCancel]
    )

    return (
        <>
            <Dialog
                open={open}
                data-testid="addProductSelection"
                maxWidth="lg"
                className={classes.dialog}
                onClose={handleClose}
            >
                <GridContainer
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <GridItem>
                        <DialogTitle className={classes.title}>
                            {'Add to Project'}
                        </DialogTitle>
                    </GridItem>
                    {processEnv.REACT_APP_EnableBadgelessCards !== '1' && (
                        <GridItem className={classes.rightContainer}>
                            <ProductListingCardLogo
                                listingLevel={listingType}
                            />
                        </GridItem>
                    )}
                </GridContainer>
                {processEnv.REACT_APP_EnableBadgelessCards !== '1' && (
                    <Typography
                        component="div"
                        className={classes.accordionWrapper}
                    >
                        <Accordion
                            className={
                                isInfoExpanded
                                    ? classes.infoContainerExpanded
                                    : classes.infoContainerCollapsed
                            }
                            style={{ borderRadius: 10 }}
                            onChange={() => setIsInfoExpanded(!isInfoExpanded)}
                            defaultExpanded={isInfoExpanded}
                        >
                            <AccordionSummary
                                aria-expanded={isInfoExpanded}
                                expandIcon={
                                    isInfoExpanded ? (
                                        <Typography
                                            className={
                                                classes.expandIconWrapper
                                            }
                                        >
                                            <Close />
                                        </Typography>
                                    ) : (
                                        <Typography
                                            className={
                                                classes.expandIconWrapper
                                            }
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="1em"
                                                height="1em"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    fill="currentColor"
                                                    d="M5 19v-6h2v4h4v2zm12-8V7h-4V5h6v6z"
                                                />
                                            </svg>
                                        </Typography>
                                    )
                                }
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                                role="button"
                                className={classes.infoContainerTitle}
                            >
                                <Typography
                                    variant="subtitle2"
                                    className={classes.dialogContentFontStyle}
                                >
                                    <InfoOutlined
                                        className={classes.infoIcon}
                                    />
                                    {isGeneralDistribution
                                        ? 'General Distribution Listing Information'
                                        : 'MasterSpec Listing Information'}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                                className={classes.infoContainerBody}
                            >
                                {isGeneralDistribution ? (
                                    <Typography
                                        className={
                                            classes.dialogContentFontStyle
                                        }
                                        component="div"
                                    >
                                        {' '}
                                        {'This product has been reviewed for '}
                                        <b>{'appropriate placement'}</b>
                                        {
                                            ' within this section only. Compliance with '
                                        }
                                        <b>
                                            {
                                                'life safety, fire, and specific Product Type requirements has not been confirmed. '
                                            }
                                        </b>
                                        {
                                            "Verify the product's availability, suitability, and compliance with your project requirements."
                                        }
                                        <Button
                                            onClick={handleMoreInfo}
                                            className={classes.linkButton}
                                        >
                                            [More Info]
                                        </Button>
                                    </Typography>
                                ) : (
                                    <Typography
                                        className={
                                            classes.dialogContentFontStyle
                                        }
                                        component="div"
                                    >
                                        {'A '}
                                        <b>{'MasterSpec'}</b>
                                        {
                                            ' specification writer has verified that this product meets life safety, fire and Product Type requirements based on information provided by the manufacturer at the time of listing. Verify the product’s availability, suitability and compliance with your project requirements.'
                                        }
                                        <Button
                                            onClick={handleMoreInfo}
                                            className={classes.linkButton}
                                        >
                                            [More Info]
                                        </Button>
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Typography>
                )}
                <DialogContent className={classes.dialogContent} tabIndex={0}>
                    <div className={clsx([classes.queryContainer])}>
                        <Typography
                            className={classes.instructionText}
                            component="div"
                        >
                            {'What do you want to add to your project?'}
                        </Typography>
                    </div>
                    {hasManufacturerOptions && (
                        <div
                            className={clsx([
                                classes.contentContainer,
                                classes.noteContainer,
                            ])}
                        >
                            <Typography
                                className={classes.contentSecondaryTitle}
                                component="div"
                            >
                                {'NOTE: Manufacturer Options Available'}
                            </Typography>
                            <Typography
                                className={classes.instructionText}
                                component="div"
                            >
                                {
                                    "This product has Manufacturer options that can be added to your project. Adding Manufacturer options may introduce proprietary aspects to the product you are adding. Additional research may be required for bidding purposes. To learn more about the product, reach out to the contact located on the product card's resources tab."
                                }
                            </Typography>
                        </div>
                    )}

                    {!isGeneralDistribution && (
                        <div
                            className={clsx([
                                classes.contentContainer,
                                hasManufacturerOptions
                                    ? classes.mfrAndProdContainer
                                    : classes.mfrAndProdContainerNoMO,
                            ])}
                        >
                            <Typography
                                className={classes.contentSecondaryTitle}
                                component="div"
                            >
                                {'Manufacturer and Product'}
                            </Typography>
                            <Typography
                                className={classes.instructionText}
                                component="div"
                            >
                                {
                                    'Add the manufacturer and the product to the project.'
                                }
                            </Typography>
                            <Typography
                                component="div"
                                className={classes.switchContainer}
                            >
                                <FormControlLabel
                                    control={
                                        <ToggleSwitch
                                            name="mfrAndProdProperties"
                                            checked={
                                                toggleStates.isMfrProdPropsSelected
                                            }
                                            size={'small'}
                                            onChange={toggleOne}
                                            disabled={false}
                                            data-testid="MfrProd-PropertiesToggle"
                                            sliderTrackColor={
                                                toggleStates.isMfrProdPropsSelected
                                                    ? sliderColor
                                                    : 'transparent'
                                            }
                                        />
                                    }
                                    label={
                                        <Typography
                                            className={classes.instructionText}
                                            component="span"
                                        >
                                            {'Include properties'}
                                        </Typography>
                                    }
                                    labelPlacement="start"
                                    className={classes.switchLabel}
                                />
                                {hasManufacturerOptions && (
                                    <FormControlLabel
                                        control={
                                            <ToggleSwitch
                                                name="mfrAndProdOptions"
                                                checked={
                                                    toggleStates.isMfrProdOptionsSelected
                                                }
                                                size={'small'}
                                                onChange={toggleOne}
                                                disabled={false}
                                                data-testid="MfrProd-MfrOptionsToggle"
                                                sliderTrackColor={
                                                    toggleStates.isMfrProdOptionsSelected
                                                        ? sliderColor
                                                        : 'transparent'
                                                }
                                            />
                                        }
                                        label={
                                            <Typography
                                                className={
                                                    classes.instructionText
                                                }
                                                component="span"
                                            >
                                                {'Include Manufacturer options'}
                                            </Typography>
                                        }
                                        labelPlacement="start"
                                        className={classes.switchLabel}
                                    />
                                )}
                            </Typography>
                            <div className={classes.buttonContainer}>
                                <Button
                                    data-testid="btnSelectMfrProd"
                                    disableRipple
                                    className={clsx([
                                        classes.button,
                                        classes.addToProjectButton,
                                    ])}
                                    onClick={() =>
                                        onMfrAndProductClick(toggleStates)
                                    }
                                >
                                    {'Add to Project'}
                                </Button>
                            </div>
                        </div>
                    )}
                    <div
                        className={clsx([
                            classes.contentContainer,
                            hasManufacturerOptions
                                ? classes.mfrOnlyContainer
                                : classes.mfrOnlyContainerNoMO,
                        ])}
                    >
                        <Typography
                            className={classes.contentSecondaryTitle}
                            component="div"
                        >
                            {'Manufacturer Only'}
                        </Typography>
                        <Typography
                            className={classes.instructionText}
                            component="div"
                        >
                            {isGeneralDistribution
                                ? 'Add the manufacturer to the project.'
                                : 'Add the manufacturer to the project but do not add the product.'}
                        </Typography>
                        <Typography
                            component="div"
                            className={classes.switchContainer}
                        >
                            <FormControlLabel
                                control={
                                    <ToggleSwitch
                                        name="mfrOnlyProperties"
                                        checked={
                                            toggleStates.isMfrOnlyPropsSelected
                                        }
                                        size={'small'}
                                        onChange={toggleOne}
                                        disabled={false}
                                        data-testid="MfrOnly-PropertiesToggle"
                                        sliderTrackColor={
                                            toggleStates.isMfrOnlyPropsSelected
                                                ? sliderColor
                                                : 'transparent'
                                        }
                                    />
                                }
                                label={
                                    <Typography
                                        className={classes.instructionText}
                                        component="span"
                                    >
                                        {'Include properties'}
                                    </Typography>
                                }
                                labelPlacement="start"
                                className={classes.switchLabel}
                            />
                            {hasManufacturerOptions && (
                                <FormControlLabel
                                    control={
                                        <ToggleSwitch
                                            name="mfrOnlyOptions"
                                            checked={
                                                toggleStates.isMfrOnlyOptionsSelected
                                            }
                                            size={'small'}
                                            onChange={toggleOne}
                                            disabled={false}
                                            data-testid="MfrOnly-MfrOptionsToggle"
                                            sliderTrackColor={
                                                toggleStates.isMfrOnlyOptionsSelected
                                                    ? sliderColor
                                                    : 'transparent'
                                            }
                                        />
                                    }
                                    label={
                                        <Typography
                                            className={classes.instructionText}
                                            component="div"
                                        >
                                            {'Include Manufacturer options'}
                                        </Typography>
                                    }
                                    labelPlacement="start"
                                    className={classes.switchLabel}
                                />
                            )}
                        </Typography>
                        <div className={classes.buttonContainer}>
                            <Button
                                data-testid="btnSelectMfrOnly"
                                disableRipple
                                className={clsx([
                                    classes.button,
                                    classes.addToProjectButton,
                                ])}
                                onClick={() => onMfrOnlyClick(toggleStates)}
                            >
                                {'Add to Project'}
                            </Button>
                        </div>
                    </div>
                    <div
                        className={clsx([
                            classes.contentContainer,
                            hasManufacturerOptions
                                ? classes.propOnlyContainer
                                : classes.propOnlyContainerNoMO,
                        ])}
                    >
                        <Typography
                            className={classes.contentSecondaryTitle}
                            component="div"
                        >
                            {'Properties Only'}
                        </Typography>
                        <Typography
                            className={classes.instructionText}
                            component="div"
                        >
                            {isGeneralDistribution
                                ? 'Add the properties to the project but not the manufacturer.'
                                : 'Add the properties to the project but not the manufacturer or product.'}
                        </Typography>
                        <Typography
                            component="div"
                            className={classes.switchContainer}
                        >
                            {hasManufacturerOptions && (
                                <FormControlLabel
                                    control={
                                        <ToggleSwitch
                                            name="propOnlyOptions"
                                            checked={
                                                toggleStates.isPropOnlyOptionsSelected
                                            }
                                            size={'small'}
                                            onChange={toggleOne}
                                            disabled={false}
                                            data-testid="PropOnly-MfrOptionsToggle"
                                            sliderTrackColor={
                                                toggleStates.isPropOnlyOptionsSelected
                                                    ? sliderColor
                                                    : 'transparent'
                                            }
                                        />
                                    }
                                    label={
                                        <Typography
                                            className={classes.instructionText}
                                            component="span"
                                        >
                                            {'Include Manufacturer options'}
                                        </Typography>
                                    }
                                    labelPlacement="start"
                                    className={classes.switchLabel}
                                />
                            )}
                        </Typography>
                        <div className={classes.buttonContainer}>
                            <Button
                                data-testid="btnSelectPropOnly"
                                disableRipple
                                className={clsx([
                                    classes.button,
                                    classes.addToProjectButton,
                                ])}
                                onClick={() => onPropOnlyClick(toggleStates)}
                            >
                                {'Add to Project'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions className={classes.actionContainer}>
                    <Button
                        data-testid="btnCancel"
                        disableRipple
                        className={clsx([classes.button, classes.cancelButton])}
                        onClick={onCancel}
                    >
                        {CANCEL_TEXT}
                    </Button>
                </DialogActions>
            </Dialog>
            <MoreInfoDialogModal
                open={isOpenMoreInfoDialog}
                onClose={() => setIsOpenMoreInfoDialog(false)}
            ></MoreInfoDialogModal>
        </>
    )
}

export default AddProductSelectionDialogEnhanced
